import { useState } from "react";
import { Plus, Trash2, GripVertical, MapPin } from "lucide-react";
import ImagePicker from "@/components/common/ImagePicker";

export interface Region {
  name: string;
  description: string;
  image?: string;
}

interface RegionsEditorProps {
  value: Region[];
  onChange: (value: Region[]) => void;
}

export default function RegionsEditor({ value, onChange }: RegionsEditorProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const addRegion = () => {
    onChange([...value, { name: "", description: "", image: "" }]);
    setExpandedIndex(value.length);
  };

  const removeRegion = (index: number) => {
    const newValue = value.filter((_, i) => i !== index);
    onChange(newValue);
    if (expandedIndex === index) {
      setExpandedIndex(null);
    }
  };

  const updateRegion = (index: number, field: keyof Region, fieldValue: string) => {
    const newValue = [...value];
    newValue[index] = { ...newValue[index], [field]: fieldValue };
    onChange(newValue);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Regiony
          </h3>
          <p className="text-sm text-gray-500">
            Přidejte jednotlivé oblasti destinace
          </p>
        </div>
        <button
          type="button"
          onClick={addRegion}
          className="btn-secondary"
        >
          <Plus className="h-4 w-4" />
          Přidat region
        </button>
      </div>

      {value.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <MapPin className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">Zatím žádné regiony</p>
          <button
            type="button"
            onClick={addRegion}
            className="mt-4 text-primary-600 hover:text-primary-700 font-medium"
          >
            Přidat první region
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {value.map((region, index) => (
            <div
              key={index}
              className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
            >
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
              >
                <div className="flex items-center gap-3">
                  <GripVertical className="h-5 w-5 text-gray-400" />
                  {region.image && (
                    <img
                      src={region.image}
                      alt=""
                      className="h-10 w-10 rounded object-cover"
                    />
                  )}
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {region.name || `Region ${index + 1}`}
                    </p>
                    {region.description && (
                      <p className="text-sm text-gray-500 truncate max-w-md">
                        {region.description}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRegion(index);
                  }}
                  className="p-2 text-gray-400 hover:text-error-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {expandedIndex === index && (
                <div className="border-t border-gray-200 p-4 space-y-4 dark:border-gray-700">
                  <div>
                    <label className="label">Název *</label>
                    <input
                      type="text"
                      value={region.name}
                      onChange={(e) => updateRegion(index, "name", e.target.value)}
                      className="input"
                      placeholder="např. Severní Vietnam"
                    />
                  </div>

                  <div>
                    <label className="label">Popis</label>
                    <textarea
                      value={region.description}
                      onChange={(e) => updateRegion(index, "description", e.target.value)}
                      className="input"
                      rows={4}
                      placeholder="Podrobný popis regionu..."
                    />
                  </div>

                  <div>
                    <label className="label">Obrázek</label>
                    <ImagePicker
                      value={region.image || ""}
                      onChange={(url) => updateRegion(index, "image", url)}
                      folder="destinations"
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
